import { useEffect, useState } from 'react';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	DashboardGridLayout,
	LoaderContainer,
	StyledIcon,
} from '@dealstack/frontend/shared/layout/DashboardGrid/styled.tsx';
import { LeftPanel } from '@dealstack/frontend/shared/layout/DashboardGrid/SidePanels/LeftPanel.tsx';
import { RightPanel } from '@dealstack/frontend/shared/layout/DashboardGrid/SidePanels/RightPanel.tsx';
import { DashboardBodyContainer } from '@dealstack/frontend/shared/layout/DashboardGrid/SidePanels/styled.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	mainContent: React.ReactNode;
	headerActions?: React.ReactNode;
	sidebarContent?: React.ReactNode;
	rightSidebarContent?: React.ReactNode;
	pageTitle?: string;
	showSidebarToggle?: boolean;
	dataLoading?: boolean;
	leftSidebarLoading?: boolean;
	leftSidebarOpened?: boolean;
	rightSidebarOpened?: boolean;
	allowPanelsResizing?: boolean;
	hideBreadcrumbs?: boolean;
	showQuickSwitch?: boolean;
}

export function DashboardGrid({
	mainContent,
	headerActions,
	sidebarContent,
	pageTitle,
	showSidebarToggle = !!sidebarContent,
	dataLoading = false,
	leftSidebarLoading,
	rightSidebarContent,
	leftSidebarOpened,
	rightSidebarOpened,
	allowPanelsResizing,
	hideBreadcrumbs,
	showQuickSwitch,
}: Props) {
	const [isSidebarVisible, setIsSidebarVisible] = useState(true);

	useEffect(() => {
		if (leftSidebarOpened !== undefined) {
			setIsSidebarVisible(leftSidebarOpened);
		}
	}, [leftSidebarOpened]);

	if (dataLoading) {
		return (
			<LoaderContainer>
				<InlineLoader withoutBackground />
			</LoaderContainer>
		);
	}

	return (
		<DashboardGridLayout>
			<PageHeader
				hideBreadcrumbs={hideBreadcrumbs}
				showQuickSwitch={showQuickSwitch}
				pageTitle={pageTitle}
				leftIcon={
					showSidebarToggle && (
						<StyledIcon
							dataTestId={TEST_ELEMENT_IDS.TOGGLE_PANEL_ICON}
							variant={isSidebarVisible ? 'collapse-left-panel' : 'expand-left-panel'}
							size="xl"
							onClick={() => setIsSidebarVisible(!isSidebarVisible)}
						/>
					)
				}
			>
				{headerActions}
			</PageHeader>

			<DashboardBodyContainer>
				<LeftPanel
					isOpened={isSidebarVisible}
					isLoading={leftSidebarLoading}
					allowResizing={allowPanelsResizing}
				>
					{sidebarContent}
				</LeftPanel>
				{mainContent}
				<RightPanel isOpened={!!rightSidebarOpened} allowResizing={allowPanelsResizing}>
					{rightSidebarContent}
				</RightPanel>
			</DashboardBodyContainer>
		</DashboardGridLayout>
	);
}
